import { Box, Flex, Image, Text, Spinner } from "@chakra-ui/react";
import { ChatInput } from "./ChatInput";
import { useEffect, useState } from "react";
import { chatList, chatDetailList, sendChat } from "../../api";
import avatar from "../../images/Avatar.png";
import { connectChatSocket } from "../../utils/chatSocket";

export default function DmDetailPage() {
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const fetchRooms = async () => {
    try {
      setLoadingRooms(true);
      const res = await chatList();
      setRooms(res?.data?.items || []);
    } catch (e) {
      console.error("채팅방 목록 조회 실패", e);
    } finally {
      setLoadingRooms(false);
    }
  };

  // 채팅방 목록 조회
  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (loadingRooms) return;

    if (rooms.length === 0) {
      if (currentRoom) {
        setCurrentRoom(null);
        setMessages([]);
      }
      return;
    }

    if (!currentRoom) {
      setCurrentRoom(rooms[0]);
      return;
    }

    const selectedRoomExists = rooms.some(
      (room) => room.roomId === currentRoom.roomId
    );
    if (!selectedRoomExists) {
      setCurrentRoom(rooms[0]);
    }
  }, [rooms, currentRoom, loadingRooms]);

  useEffect(() => {
    const client = connectChatSocket({
      onMessage: (payload) => {
        fetchRooms();
        if (payload.roomId !== currentRoom?.roomId) return;
        setMessages((prev) => {
          if (prev.some((message) => message.messageId === payload.messageId)) {
            return prev;
          }
          return [
            ...prev,
            {
              ...payload,
              mine: false,
            },
          ];
        });
      },
    });

    return () => client?.deactivate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoom?.roomId]);

  // 채팅방 선택 시 메시지 조회
  useEffect(() => {
    if (!currentRoom) return;

    const fetchMessages = async () => {
      try {
        setLoadingMessages(true);
        const res = await chatDetailList({ id: currentRoom.roomId });
        setMessages(res?.data?.messages || []);
      } catch (e) {
        console.error("메시지 조회 실패", e);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [currentRoom]);

  const handleSendText = async (text) => {
    if (!currentRoom || !text?.trim()) return;

    const payload = {
      messageType: "TEXT",
      body: text,
    };

    // 1. optimistic message
    const tempMessage = {
      messageId: `temp-${Date.now()}`,
      mine: true,
      body: text,
      messageType: "TEXT",
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempMessage]);

    try {
      const res = await sendChat({
        id: currentRoom.roomId,
        body: payload,
      });

      // 2. 서버 응답으로 교체 or 추가
      const savedMessage = res?.data ?? tempMessage;

      setMessages((prev) =>
        prev.map((m) =>
          m.messageId === tempMessage.messageId ? savedMessage : m
        )
      );
    } catch (e) {
      console.error("메시지 전송 실패", e);

      // 실패 시 롤백
      setMessages((prev) =>
        prev.filter((m) => m.messageId !== tempMessage.messageId)
      );

      alert("메시지 전송 실패");
    }
  };

  return (
    <Flex w="100%" h="100%" bg="white" borderRadius="16px" p="20px" gap="16px">
      {/* 왼쪽 채팅방 리스트 */}
      <Flex
        w="30%"
        border="1px solid #eee"
        borderRadius="16px"
        flexDir="column"
        overflowY="auto"
      >
        {loadingRooms && <Spinner m="20px" />}

        {rooms.map((room) => (
          <Flex
            key={room.roomId}
            p="12px"
            align="center"
            gap="10px"
            cursor="pointer"
            bg={currentRoom?.roomId === room.roomId ? "#F0F4F9" : "transparent"}
            _hover={{ bg: "#F7F9FC" }}
            onClick={() => setCurrentRoom(room)}
          >
            <Image src={avatar} w="40px" h="40px" borderRadius="50%" />

            <Box flex="1" minW={0}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="700" noOfLines={1}>
                  {room.targetName || "상대방"}
                </Text>

                {room.unreadCount > 0 && (
                  <Box
                    bg="red.500"
                    color="white"
                    fontSize="10px"
                    px="6px"
                    py="2px"
                    borderRadius="999px"
                  >
                    {room.unreadCount}
                  </Box>
                )}
              </Flex>

              <Text fontSize="12px" color="#5D6979" fontWeight="600" noOfLines={1}>
                {room.targetRole === "creator" ? "크리에이터" : "광고주"}
                {room.campaignTitle ? ` · ${room.campaignTitle}` : ""}
              </Text>
              <Text fontSize="12px" color="gray.500" noOfLines={1}>
                {room.lastMessageBody || "대화 없음"}
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>

      {/* 오른쪽 채팅 영역 */}
      <Flex w="70%" border="1px solid #eee" borderRadius="16px">
        <Flex flex="1" flexDir="column">
          {currentRoom ? (
            <>
              <Box flex="1" overflowY="auto" p="16px">
                {loadingMessages && <Spinner />}

                {messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const currentDate = m.createdAt?.split("T")[0];
                  const prevDate = prev?.createdAt?.split("T")[0];
                  const showDate = i === 0 || currentDate !== prevDate;

                  const time = new Date(m.createdAt).toLocaleTimeString(
                    "ko-KR",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  );

                  return (
                    <Box key={m.messageId} mb="18px">
                      {showDate && (
                        <Flex justify="center" color="#8A8F99" fontSize="12px">
                          {currentDate}
                        </Flex>
                      )}

                      {m.mine ? (
                        <Flex justify="flex-end" gap="6px">
                          <Box
                            bg="#0054C0"
                            color="white"
                            p="10px 14px"
                            borderRadius="12px"
                            maxW="320px"
                          >
                            {m.body}
                          </Box>
                          <Text fontSize="10px" alignSelf="end">
                            {time}
                          </Text>
                        </Flex>
                      ) : (
                        <Flex align="flex-end" gap="8px">
                          <Image
                            src={avatar}
                            w="32px"
                            h="32px"
                            borderRadius="50%"
                          />
                          <Box
                            bg="#F0F4F9"
                            p="10px 14px"
                            borderRadius="12px"
                            maxW="320px"
                          >
                            {m.body}
                          </Box>
                          <Text fontSize="10px">{time}</Text>
                        </Flex>
                      )}
                    </Box>
                  );
                })}
              </Box>

              <ChatInput onSendText={handleSendText} />
            </>
          ) : (
            <Flex
              flex="1"
              justify="center"
              align="center"
              color="gray.400"
              fontWeight="700"
            >
              채팅방을 선택하세요.
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
